import { Sun, Moon, Monitor, Bell } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { usePreferences, Theme, FontSize } from '@/hooks/usePreferences';
import { useToast } from '@/hooks/use-toast';

export function PreferencesTab() {
  const { preferences, updatePreferences, isLoading } = usePreferences();
  const { toast } = useToast();

  const handleThemeChange = (theme: Theme) => {
    updatePreferences({ theme });
    toast({
      title: 'Tema actualizado',
      description: `Se ha cambiado al tema ${theme === 'light' ? 'claro' : theme === 'dark' ? 'oscuro' : 'del sistema'}`,
    });
  };

  const handleFontSizeChange = (fontSize: FontSize) => {
    updatePreferences({ fontSize });
    toast({
      title: 'Tamaño de fuente actualizado',
      description: `Tamaño cambiado a ${fontSize === 'small' ? 'pequeño' : fontSize === 'medium' ? 'mediano' : 'grande'}`,
    });
  };

  const handleNotificationsChange = (enabled: boolean) => {
    updatePreferences({ notificationsEnabled: enabled });
    toast({
      title: enabled ? 'Notificaciones activadas' : 'Notificaciones desactivadas',
      description: enabled
        ? 'Recibirás alertas cuando lleguen nuevos mensajes'
        : 'No recibirás notificaciones de nuevos mensajes',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center text-muted-foreground">
          Cargando preferencias...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-1">
          Preferencias del Chat
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Personaliza tu experiencia en Munilex
        </p>
      </div>

      {/* Tema */}
      <div className="space-y-4">
        <Label className="text-base font-medium text-foreground">Tema</Label>
        <RadioGroup
          value={preferences.theme}
          onValueChange={(value) => handleThemeChange(value as Theme)}
          className="space-y-3"
        >
          <div className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer">
            <RadioGroupItem value="light" id="theme-light" />
            <Label
              htmlFor="theme-light"
              className="flex items-center gap-3 cursor-pointer flex-1"
            >
              <Sun className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium">Claro</span>
            </Label>
          </div>

          <div className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer">
            <RadioGroupItem value="dark" id="theme-dark" />
            <Label
              htmlFor="theme-dark"
              className="flex items-center gap-3 cursor-pointer flex-1"
            >
              <Moon className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium">Oscuro</span>
            </Label>
          </div>

          <div className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer">
            <RadioGroupItem value="system" id="theme-system" />
            <Label
              htmlFor="theme-system"
              className="flex items-center gap-3 cursor-pointer flex-1"
            >
              <Monitor className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium">Sistema</span>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Tamaño de fuente */}
      <div className="space-y-4">
        <Label className="text-base font-medium text-foreground">
          Tamaño de fuente
        </Label>
        <RadioGroup
          value={preferences.fontSize}
          onValueChange={(value) => handleFontSizeChange(value as FontSize)}
          className="space-y-3"
        >
          <div className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer">
            <RadioGroupItem value="small" id="font-small" />
            <Label
              htmlFor="font-small"
              className="flex items-center gap-3 cursor-pointer flex-1"
            >
              <span className="text-xs font-bold text-muted-foreground">T</span>
              <span className="text-sm font-medium">Pequeño</span>
            </Label>
          </div>

          <div className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer">
            <RadioGroupItem value="medium" id="font-medium" />
            <Label
              htmlFor="font-medium"
              className="flex items-center gap-3 cursor-pointer flex-1"
            >
              <span className="text-sm font-bold text-muted-foreground">T</span>
              <span className="text-sm font-medium">Mediano</span>
            </Label>
          </div>

          <div className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer">
            <RadioGroupItem value="large" id="font-large" />
            <Label
              htmlFor="font-large"
              className="flex items-center gap-3 cursor-pointer flex-1"
            >
              <span className="text-base font-bold text-muted-foreground">T</span>
              <span className="text-sm font-medium">Grande</span>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Notificaciones */}
      <div className="space-y-4">
        <Label className="text-base font-medium text-foreground">
          Notificaciones
        </Label>
        <div className="flex items-center justify-between p-4 rounded-lg border border-border">
          <div className="flex items-center gap-3">
            <Bell className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-foreground">
                Notificaciones en tiempo real
              </p>
              <p className="text-xs text-muted-foreground">
                Recibe alertas cuando lleguen nuevos mensajes
              </p>
            </div>
          </div>
          <Switch
            checked={preferences.notificationsEnabled}
            onCheckedChange={handleNotificationsChange}
          />
        </div>
      </div>
    </div>
  );
}
